/**
 * Pure, Vue-free, side-effect-free helpers for settings display +
 * inheritance resolution. Unit-testable in isolation.
 *
 * Replaces `ScopedSettingUtils` (the old static-class version) —
 * static classes are just namespaces in disguise, and these functions
 * tree-shake better as plain exports.
 */

import type {
  AgencyStructureNode,
  ScopedSetting,
  SettingDefinition,
  SettingName
} from '@gat-ship/types'

/**
 * The value a consumer should show for this scoped setting — walking
 * the inheritance chain when the scope itself isn't `assigned`.
 * Returns `null` when forbidden or when the chain is empty.
 *
 * Generic over the value type (what `ScopedSetting` carries), not the
 * setting name — the name → value mapping is the caller's concern.
 */
export function getScopedSettingValue<TValue>(
  scopedSetting: ScopedSetting<TValue> | undefined
): TValue | null {
  if (!scopedSetting) return null
  if (scopedSetting.layerStatus === 'assigned') {
    return scopedSetting.value
  }
  if (scopedSetting.layerStatus === 'inherited') {
    const firstParent = scopedSetting.parentChain[0]
    return (firstParent?.value as TValue | undefined) ?? null
  }
  return null
}

/**
 * What to label the "Inherit" radio option with: if the setting would
 * fall through to the Gatship default, say so explicitly; otherwise
 * "Inherit From" (the parent agency in `parentChain[0]`).
 *
 * Value-type-agnostic — only looks at the layer/scope metadata.
 */
export function getInheritLabel(
  scopedSetting: ScopedSetting<unknown> | undefined
): string {
  const firstParent = scopedSetting?.parentChain[0]
  if (firstParent?.layerScope === 'default') return 'Use Gatship Default'
  return 'Inherit From'
}

/**
 * Resolve the "inherited from X — value Y" details shown inside the
 * inherit-radio card. Walks the parentChain past the currently-
 * selected agency (which, in assigned state, would be the first link)
 * and returns the first link that actually represents an inherited
 * source.
 */
export interface InheritedFromDetails {
  value: unknown | 'Not set'
  inheritedFrom: string | undefined
  inheritedFromId: string | null
}

export function resolveInheritedFrom(
  scopedSetting: ScopedSetting<unknown> | undefined,
  agencyStructure: readonly AgencyStructureNode[],
  selectedAgencyId: string | undefined
): InheritedFromDetails | undefined {
  if (!scopedSetting) return undefined
  const rootNode = agencyStructure.find(
    (node) => node.$type === 'agency-group' && node.isRoot
  )
  if (scopedSetting.parentChain.length === 0) {
    return {
      value: 'Not set',
      inheritedFrom: rootNode?.name,
      inheritedFromId: rootNode?.id ?? null
    }
  }
  for (const parent of scopedSetting.parentChain) {
    if (parent.scopeEntityId === selectedAgencyId) continue
    let inheritedFrom = 'Unknown'
    if (parent.layerScope === 'agency') {
      inheritedFrom = parent.scopeEntityName ?? 'Unknown'
    } else if (parent.layerScope === 'default') {
      inheritedFrom = 'Gatship Default'
    } else if (parent.layerScope === 'global') {
      inheritedFrom = 'Global Settings'
    } else if (parent.layerScope === 'user') {
      inheritedFrom = 'User Settings'
    }
    return {
      value: parent.value,
      inheritedFrom,
      inheritedFromId: parent.scopeEntityId
    }
  }
  return undefined
}

/**
 * Whether the user should be allowed to pick "Inherit" for this
 * setting at the currently-selected agency.
 *
 * Two modes, in priority order:
 * 1. `preventRemovalWhenLayerIsAgencyRoot=true` — the setting must be
 *    assigned at the root, but every non-root agency inherits by
 *    default and can override. `hasDefaultValue` doesn't apply here;
 *    the root acts as the default.
 * 2. Otherwise — fall back to `hasDefaultValue` (there has to be
 *    *something* to inherit from).
 */
export function canRevertToInherit(
  definition: SettingDefinition<SettingName> | undefined,
  agencyStructure: readonly AgencyStructureNode[],
  selectedAgencyId: string | undefined
): boolean {
  if (!definition) return false
  const { hasDefaultValue, preventRemovalWhenLayerIsAgencyRoot } = definition
  if (preventRemovalWhenLayerIsAgencyRoot) {
    const rootNode = agencyStructure.find(
      (node) => node.$type === 'agency-group' && node.isRoot
    )
    const isAgencyRoot =
      selectedAgencyId !== undefined && rootNode?.id === selectedAgencyId
    return !isAgencyRoot
  }
  return hasDefaultValue
}
